// A container of continuous intervals over the real number line.
//   insert(span): Takes the set-wise union of the contained intervals with the span.
//   remove(span): Takes the set-wise difference of the contained intervals with the span.
export default class Interval {
  constructor() {
    this.spans = [];
    this.roots = [];
  }

  clear() {
    this.spans.length = 0;
  }

  // Returns the largest interval containing the newly-inserted span,
  //  or null if the span is null.
  insert(span, position) {
    var node = {
      span: [span[0], span[1]],
      width: [position, NaN],
      children: [],
    };

    for (var i = this.spans.length - 1; i >= 0; i -= 1) {
      var subspan = this.spans[i];

      if (node.span[1] < subspan.span[0]) {
      // This subspan is entirely unaffected by this span.
        continue;
      } else if (subspan.span[1] < node.span[0]) {
      // This subspan is entirely unaffected, and no later subspan is affected.
        this.spans.splice(i+1, 0, node);
        return;
      } else if (node.span[0] <= subspan.span[0]) {
      // The subspan is being extended leftwards.
        if (position === subspan.width[0]) {
          node.children.push.apply(node.children, subspan.children);
        } else {
          node.children.push(subspan);
          subspan.width[1] = position;
        }
        node.span[1] = Math.max(subspan.span[1], node.span[1]);
        this.spans.splice(i, 1);
        continue;
      } else if (node.span[0] <= subspan.span[1]) {
      // The subspan is being extended rightwards, and no other subspan is affected.
        if (position === subspan.width[0]) {
          node.children.push.apply(node.children, subspan.children);
        } else {
          node.children.push(subspan);
          subspan.width[1] = position;
        }
        node.span[0] = Math.min(subspan.span[0], node.span[0]);
        this.spans.splice(i, 1, node);
        return;
      } else {
      // This subspan is entirely contained within this span, and no other subspan is affected.
        if (position === subspan.width[0]) {
          subspan.span[0] = node.span[0];
          subspan.span[1] = node.span[1];
        } else {
          node.children.push(subspan);
          subspan.width[1] = position;
        }
        return;
      }
    }

    // This span is brand-new.
    this.spans.splice(0, 0, node);
  }

  // Removes the given span.
  remove(span, position) {
    for (var i = this.spans.length - 1; i >= 0; i -= 1) {
      let subspan = this.spans[i];

      if (span[1] <= subspan.span[0]) {
      // This subspan is entirely unaffected by this span.
        continue;
      } else if (subspan.span[1] <= span[0]) {
      // This subspan is entirely unaffected, and no later subspan is affected.
        return;
      } else if (span[0] <= subspan.span[0] && subspan.span[1] <= span[1]) {
      // This subspan is covered entirely.
        if (position === subspan.width[0]) {
          // A zero-width, zero-span region? Impossible!
          // (but no, really, this is equivalent to just having an unbroken wall capping the subspan's children.)
          this.roots.push.apply(this.roots, subspan.children);
        } else {
          // This region is being capped fully.
          this.roots.push(subspan);
          subspan.width[1] = position;
        }
        this.spans.splice(i, 1);
        continue;
      } else if (span[0] <= subspan.span[0]) {
      // This subspan is being truncated from the left.
        if (position === subspan.width[0] && (subspan.children.length <= 0 || subspan.span[1] <= subspan.children[subspan.children.length-1].span[1])) {
          subspan.span[0] = span[1];
        } else {
          this.spans.splice(i, 1, {
            span: [span[1], subspan.span[1]],
            width: [position, NaN],
            children: [subspan],
          });
          subspan.width[1] = position;
        }
        continue;
      } else if (subspan.span[1] <= span[1]) {
      // This subspan is being truncated from the right, and no later subspan is affected.
        if (position === subspan.width[0] && (subspan.children.length <= 0 || subspan.children[0].span[0] <= subspan.span[0])) {
          subspan.span[1] = span[0];
        } else {
          this.spans.splice(i, 1, {
            span: [subspan.span[0], span[0]],
            width: [position, NaN],
            children: [subspan],
          });
          subspan.width[1] = position;
        }
        return;
      } else {
      // This subspan is being split, and no later subspan is affected.
        if (position === subspan.width[0] && (subspan.children.length <= 0 || (subspan.children[0].span[0] <= subspan.span[0] && subspan.span[1] <= subspan.children[subspan.children.length-1].span[1]))) {
          this.spans.splice(i, 1, {
            span: [subspan.span[0], span[0]],
            width: [position, NaN],
            children: subspan.children,
          }, {
            span: [span[1], subspan.span[1]],
            width: [position, NaN],
            children: subspan.children,
          });
        } else {
          this.spans.splice(i, 1, {
            span: [subspan.span[0], span[0]],
            width: [position, NaN],
            children: [subspan],
          }, {
            span: [span[1], subspan.span[1]],
            width: [position, NaN],
            children: [subspan],
          });
        }
        subspan.width[1] = position;
        return;
      }
    }
  }
}
